import { Button, Modal } from '@clearcover/atlas';
import { MultiLineDiscountEnumType } from '@clearcover/core-app-types';
import {
  editMultiLineDiscountPolicy,
  editTelematicsDiscount,
} from 'Admin/authPolicies';
import { usePolicyContext } from 'Policies/utils/PolicyContext/PolicyContext';
import React, { FC, useState } from 'react';
import { Card } from 'Shared/components';
import { EMDASH } from 'Shared/utils';
import { ConflictingDraftModal } from '../ConflictingDraftModal';
import {
  EditDiscountForm,
  EditTelematicsDiscountForm,
} from './EditDiscountForm';
import styles from './style.module.css';

type DiscountType = {
  id: string;
  applied: string;
  name: string;
  type: string;
  allocation: string;
};
export type mldDiscountType = {
  type: MultiLineDiscountEnumType;
  name: string;
  applied?: string;
};

type PolicyDiscountType = {
  discounts: Array<DiscountType>;
  mldDiscount: mldDiscountType | undefined;
};

export const TelematicsDiscountEditModal: FC<{
  hasTelematicsDiscount: boolean;
}> = ({ hasTelematicsDiscount }) => {
  return (
    <div>
      <Modal
        variant={'normal'}
        toggle={toggle => (
          <Button
            data-testid={'edit-telematics-button'}
            className={styles.edit}
            isSubmitting={false}
            link
            size="sm"
            onClick={toggle}
          >
            Edit
          </Button>
        )}
      >
        {close => (
          <EditTelematicsDiscountForm
            closeModal={close}
            hasTelematicsDiscount={hasTelematicsDiscount}
          />
        )}
      </Modal>
    </div>
  );
};

export const PolicyDiscountSection: FC<PolicyDiscountType> = ({
  discounts,
  mldDiscount,
}) => {
  const [open, setOpen] = useState(false);
  const {
    isEndorseablePolicy,
    systemOfRecord,
    resourceEffectiveGrants,
  } = usePolicyContext();

  const canEditMultilineDiscount =
    editMultiLineDiscountPolicy(resourceEffectiveGrants) && isEndorseablePolicy;

  const canEditTelematicsDiscount = editTelematicsDiscount(
    resourceEffectiveGrants
  );

  // Band-aid fix for https://clearcover.atlassian.net/browse/PIE-900, patching it here cause it this can come from the policy or draft
  const laMilitaryDiscountIndex = discounts.findIndex(
    ({ id }) => id === 'laMilitaryDiscount'
  );
  if (laMilitaryDiscountIndex > 0) {
    discounts[laMilitaryDiscountIndex] = {
      ...discounts[laMilitaryDiscountIndex],
      name: 'LA Military Discount',
    };
  }

  function isMultiLineDiscount(discount: DiscountType): boolean {
    return discount.id === 'multiLineDiscount';
  }

  const mldRatingDiscount = discounts.find(discount =>
    isMultiLineDiscount(discount)
  );
  const mldDiscountEnabled = mldRatingDiscount !== undefined;

  return (
    <div>
      {discounts.length !== 0 || mldDiscount ? (
        <Card id={'discount-card'}>
          <h2>Discounts</h2>
          <ul className={styles.list}>
            <li key="title" className={styles.headers}>
              <span className={styles.header}>Applied</span>
              <span className={styles.header}>Name</span>
              <span className={styles.header}>Type</span>
              <span className={styles.header}>Allocation</span>
            </li>
            {systemOfRecord === 'clearcover' && mldDiscountEnabled && (
              <li data-testid={`mld-discount`} className={styles.discount}>
                <div className={styles.data}>
                  {mldRatingDiscount?.applied ?? 'No'}
                </div>
                <div className={styles.data}>Multi-line Discount</div>
                <div className={styles.data}>Policy</div>
                <div className={styles.data}>{mldDiscount?.name ?? EMDASH}</div>
                <div>
                  {canEditMultilineDiscount && (
                    <Modal
                      variant={'normal'}
                      toggle={openEditDiscountsModal => (
                        <ConflictingDraftModal
                          onApprove={openEditDiscountsModal}
                          renderToggle={openModal => (
                            <Button
                              data-testid={'edit-mld-button'}
                              className={styles.edit}
                              isSubmitting={false}
                              link
                              size="sm"
                              onClick={openModal}
                            >
                              Edit
                            </Button>
                          )}
                        />
                      )}
                    >
                      {close => (
                        <EditDiscountForm
                          closeModal={close}
                          mldDiscount={mldDiscount}
                        />
                      )}
                    </Modal>
                  )}
                </div>
              </li>
            )}
            {discounts
              .filter(discount => !isMultiLineDiscount(discount))
              .filter(discount => open || discount.applied === 'Yes')
              .map(entries => {
                return entries.applied === 'No' ? (
                  <li
                    data-testid={`discount-${entries.id}`}
                    key={`${entries.id}-${entries.allocation}`}
                    className={styles.discount}
                  >
                    <div className={styles.no}>{entries.applied}</div>
                    <div className={styles.no}>
                      {entries.id === 'digitalDriverDiscount'
                        ? 'ClearClaims+ Discount'
                        : entries.name}
                    </div>
                    <div className={styles.no}>{entries.type}</div>
                    <div className={styles.no}>
                      {entries.allocation || EMDASH}
                    </div>
                    {/* Uncomment this code when add discount logic is finished */}
                    {/* {canEditTelematicsDiscount &&
                      entries.id === 'digitalDriverDiscount' && (
                        <TelematicsDiscountEditModal
                          hasTelematicsDiscount={false}
                        />
                      )} */}
                  </li>
                ) : (
                  <li
                    data-testid={`discount-${entries.id}`}
                    key={`${entries.id}-${entries.allocation}`}
                    className={styles.discount}
                  >
                    <div className={styles.data}>{entries.applied}</div>
                    <div className={styles.data}>
                      {entries.id === 'digitalDriverDiscount'
                        ? 'ClearClaims+ Discount'
                        : entries.name}
                    </div>
                    <div className={styles.data}>{entries.type}</div>
                    <div className={styles.data}>
                      {entries.allocation || EMDASH}
                    </div>
                    {canEditTelematicsDiscount &&
                      entries.id === 'digitalDriverDiscount' && (
                        <TelematicsDiscountEditModal
                          hasTelematicsDiscount={true}
                        />
                      )}
                  </li>
                );
              })}
          </ul>
          <div>
            <Button
              data-testid={'view-additional-discount-button'}
              className={styles.toggleButton}
              isSubmitting={false}
              link
              size="sm"
              onClick={() => {
                setOpen(currValue => !currValue);
              }}
            >
              {open ? 'Hide' : 'View'} additional discounts
            </Button>
          </div>
        </Card>
      ) : (
        ''
      )}
    </div>
  );
};
export default PolicyDiscountSection;
